import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Tabs, Tab, Row, Col, Badge, Button } from 'react-bootstrap';
import { useQuery } from 'react-query';
import moment from 'moment';

import { HOPDONGCHITIET_API, LICHHOCTHUCHANH_API, ACCOUNT_API, ACCOUNT_AUTHINFO_API, DONVI_API, NGANHDAOTAO_API } from '../../../utils/apiUrl';
import { Get } from '../../../utils/httpProvider';
import { userManager } from '../../../utils/oidc';

import ScheduleTab from './components/ScheduleTab';
import StudentTab from './components/StudentTab';
import TutorTab from './components/TutorTab';
import MaterialsAndTopicsTab from './components/MaterialsAndTopicsTab';

const KhoaDaoTaoDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const [activeTab, setActiveTab] = useState('general');

    const contractId = id;

    // Roles and ID from OIDC token to check if current user is a student or instructor
    const [currentUserRoles, setCurrentUserRoles] = useState([]);
    const [currentUserSub, setCurrentUserSub] = useState('');

    useEffect(() => {
        const fetchOidcInfo = async () => {
            const user = await userManager.getUser();
            if (user?.profile) {
                if (user.profile.role) {
                    const roles = Array.isArray(user.profile.role)
                        ? user.profile.role
                        : [user.profile.role];
                    setCurrentUserRoles(roles);
                }
                if (user.profile.sub) {
                    setCurrentUserSub(user.profile.sub);
                }
            }
        };
        fetchOidcInfo();
    }, []);

    // --- FETCH DETAILED COURSE DATA ---
    const { data: courseDetailData, isLoading: isCourseLoading } = useQuery(
        ['courseDetail', id],
        () => Get(`${HOPDONGCHITIET_API.GET_ITEM}/${id}`),
        {
            enabled: !!id
        }
    );

    // --- FETCH SCHEDULE DATA ---
    const { data: scheduleData, isLoading: isScheduleLoading, refetch: refetchSchedule } = useQuery(
        ['scheduleList', contractId],
        () => Get(`${LICHHOCTHUCHANH_API.GET_BY_COURSE}/${contractId}`),
        {
            enabled: !!contractId,
            select: (res) => res?.$values || res || []
        }
    );

    // --- FETCH STUDENTS DATA ---
    const { data: studentsData, isLoading: isStudentsLoading, refetch: refetchStudents } = useQuery(
        ['studentsList', contractId],
        async () => {
            const res = await Get(`${HOPDONGCHITIET_API.GET_HOCVIENS}/${contractId}`);
            const accountIds = res?.$values || res || [];

            const detailTasks = accountIds.map(async (accountId) => {
                try {
                    const accountInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, accountId);
                    const profile = accountInfo?.accountAuthInfo || accountInfo?.AccountAuthInfo || accountInfo;

                    const lastName = profile?.lastName || profile?.LastName || "";
                    const middleName = profile?.middleName || profile?.MiddleName || "";
                    const firstName = profile?.firstName || profile?.FirstName || "";
                    const fullName = [lastName, middleName, firstName]
                        .map(n => n?.trim())
                        .filter(Boolean)
                        .join(" ");

                    return {
                        id: accountId,
                        accountId: accountId,
                        fullName: fullName || "Chưa xác định",
                        phoneNumber: profile?.phoneNumber || profile?.PhoneNumber || "",
                        identityCard: profile?.identityCardNumber || profile?.IdentityCardNumber || profile?.identityCard || profile?.IdentityCard || "",
                        pseudonym: profile?.pseudonym || profile?.Pseudonym || "",
                        email: profile?.email || profile?.Email || "",
                        address: profile?.address || profile?.Address || ""
                    };
                } catch (err) {
                    return { id: accountId, accountId: accountId, fullName: "Lỗi tải thông tin" };
                }
            });

            return await Promise.all(detailTasks);
        },
        {
            enabled: !!contractId
        }
    );

    // --- FETCH TUTORS DATA ---
    const { data: tutorsData, isLoading: isTutorsLoading, refetch: refetchTutors } = useQuery(
        ['tutorsList', contractId],
        async () => {
            const res = await Get(`${HOPDONGCHITIET_API.GET_GIANGVIENS}/${contractId}`);
            const accountIds = res?.$values || res || [];

            const detailTasks = accountIds.map(async (accountId) => {
                try {
                    const accountInfo = await Get(ACCOUNT_AUTHINFO_API.GET_BY_ID_ACCOUNT, accountId);
                    const profile = accountInfo?.accountAuthInfo || accountInfo?.AccountAuthInfo || accountInfo;

                    const lastName = profile?.lastName || profile?.LastName || "";
                    const middleName = profile?.middleName || profile?.MiddleName || "";
                    const firstName = profile?.firstName || profile?.FirstName || "";
                    const fullName = [lastName, middleName, firstName]
                        .map(n => n?.trim())
                        .filter(Boolean)
                        .join(" ");

                    return {
                        id: accountId,
                        accountId: accountId,
                        fullName: fullName || "Chưa xác định",
                        phoneNumber: profile?.phoneNumber || profile?.PhoneNumber || "",
                        identityCard: profile?.identityCardNumber || profile?.IdentityCardNumber || profile?.identityCard || profile?.IdentityCard || "",
                        pseudonym: profile?.pseudonym || profile?.Pseudonym || "",
                        email: profile?.email || profile?.Email || ""
                    };
                } catch (err) {
                    return { id: accountId, accountId: accountId, fullName: "Lỗi tải thông tin" };
                }
            });

            return await Promise.all(detailTasks);
        },
        {
            enabled: !!contractId
        }
    );

    const isStudentUser = studentsData && studentsData.some(s => s.id === currentUserSub);
    const isInstructorUser = tutorsData && tutorsData.some(t => t.id === currentUserSub);

    // --- FETCH ALL REQUESTS FOR ADMIN ---
    const { data: adminRequestsData, isLoading: isAdminRequestsLoading } = useQuery(
        ['adminRequestsList', contractId],
        () => Get(`${LICHHOCTHUCHANH_API.GET_YEU_CAU_FOR_ADMIN}/${contractId}`).then(res => {
            const raw = res?.data || res;
            return raw?.$values || raw || [];
        }),
        {
            enabled: !!contractId && !isStudentUser && !isInstructorUser
        }
    );

    // --- FETCH DEPARTMENTS, MAJORS AND KHOA MAPPINGS ---
    const { data: donViData } = useQuery(
        'donViList',
        () => Get(DONVI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        {
            select: (res) => res?.data?.$values || res?.$values || res?.data || res || []
        }
    );

    const { data: nganhDaoTaoData } = useQuery(
        'nganhDaoTaoList',
        () => Get(NGANHDAOTAO_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }),
        {
            select: (res) => res?.data?.$values || res?.$values || res?.data || res || []
        }
    );

    const { data: mappingData, refetch: refetchMappings } = useQuery(
        'excelKhoaMappings',
        () => Get(LICHHOCTHUCHANH_API.GET_EXCEL_KHOA_MAPPINGS),
        {
            select: (res) => res?.$values || res || []
        }
    );

    // --- FETCH MATERIALS DATA ---
    const { data: materialsData, isLoading: isMaterialsLoading, refetch: refetchMaterials } = useQuery(
        ['materialsList', id],
        () => Get(`${HOPDONGCHITIET_API.GET_TAILIEUS}/${id}`),
        {
            enabled: !!id,
            select: (res) => res?.$values || res || []
        }
    );

    // --- FETCH TRAINING TOPICS ---
    const { data: topicsData, isLoading: isTopicsLoading, refetch: refetchTopics } = useQuery(
        ['topicsList', id],
        () => Get(`${HOPDONGCHITIET_API.GET_CHUONGTRINHS}/${id}`),
        {
            enabled: !!id,
            select: (res) => res?.$values || res || []
        }
    );

    if (isCourseLoading) {
        return (
            <div className="container-fluid p-5 text-center">
                <div className="spinner-border text-primary mb-3" role="status"></div>
                <h5 className="text-muted">Đang tải dữ liệu khóa học...</h5>
            </div>
        );
    }

    if (!courseDetailData) {
        return (
            <div className="container-fluid p-4">
                <Button variant="link" onClick={() => history.push('/khoa-dao-tao')} className="mb-3 p-0 text-decoration-none">
                    <i className="fas fa-arrow-left mr-1"></i> Quay lại danh sách
                </Button>
                <div className="alert alert-warning shadow-sm">Không tìm thấy thông tin khóa đào tạo.</div>
            </div>
        );
    }

    const item = courseDetailData;
    const rawSchool = item.truongThucTapTen || item.TruongThucTapTen || item.hopDongChiTiet?.hopDongNguyenTac?.truongThucTap?.tenTruong || item.hopDongChiTiet?.hopDongNguyenTac?.truongThucTap?.TenTruong || '';
    const schoolName = (rawSchool === '-' || rawSchool === 'Chưa có') ? '' : rawSchool;

    const rawProgram = item.tenKhoaDaoTao || item.TenKhoaDaoTao || item.tenHopDong || item.TenHopDong || item.chuongTrinhDaoTaoTen || item.ChuongTrinhDaoTaoTen || '';
    const programName = (rawProgram === '-' || rawProgram === 'Chưa có') ? '' : rawProgram;

    const dateStart = (item.ngayKy || item.NgayKy) ? moment(item.ngayKy || item.NgayKy).format('DD/MM/YYYY') : '';
    const dateEnd = (item.ngayKetThuc || item.NgayKetThuc) ? moment(item.ngayKetThuc || item.NgayKetThuc).format('DD/MM/YYYY') : '';

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="font-weight-bold mb-1 text-dark" style={{ letterSpacing: '-0.5px' }}>
                        {programName || 'Chương trình thực hành lâm sàng'}
                    </h3>
                    <div className="text-muted small">
                        <i className="fas fa-hospital mr-1"></i> {schoolName || 'Bệnh viện thực tập'}
                    </div>
                </div>
                <Button variant="outline-secondary" onClick={() => history.push('/khoa-dao-tao')} style={{ borderRadius: '8px' }}>
                    <i className="fas fa-chevron-left mr-1"></i> Quay lại
                </Button>
            </div>

            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <Card.Body className="p-0">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        id="course-detail-tabs"
                        className="custom-tabs border-bottom px-4 pt-3 bg-light"
                    >
                        {/* TAB 1: THÔNG TIN CHUNG & LỊCH HỌC CHI TIẾT */}
                        <Tab eventKey="general" title={<span><i className="fas fa-info-circle mr-2 text-primary"></i>Thông tin chung</span>}>
                            <div className="p-4">
                                <Row className="mb-4">
                                    <Col md={4} className="mb-3">
                                        <div className="text-muted small">Tên khóa đào tạo</div>
                                        <div className="font-weight-bold text-dark mt-1" style={{ fontSize: '1rem' }}>{programName || '-'}</div>
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <div className="text-muted small">Mã & Tên hợp đồng chi tiết</div>
                                        <div className="font-weight-bold text-dark mt-1">{item.soHopDong || '-'} - {item.tenHopDong || '-'}</div>
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <div className="text-muted small">Chuyên ngành đào tạo</div>
                                        <div className="mt-1">
                                            <Badge bg="info" className="text-dark mt-1" style={{ fontSize: '0.85rem', padding: '5px 10px' }}>
                                                {item.nganhDaoTaoTen || item.NganhDaoTaoTen || 'Chưa rõ'} - {item.chuongTrinhDaoTaoTen || item.ChuongTrinhDaoTaoTen || 'Chưa rõ'}
                                            </Badge>
                                        </div>
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <div className="text-muted small">Đối tượng học viên</div>
                                        <div className="font-weight-bold text-dark mt-1">{item.doiTuong || '-'}</div>
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <div className="text-muted small">Thời gian bắt đầu</div>
                                        <div className="font-weight-bold text-primary mt-1"><i className="far fa-calendar-alt mr-1"></i> {dateStart || '-'}</div>
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <div className="text-muted small">Thời gian kết thúc</div>
                                        <div className="font-weight-bold text-danger mt-1"><i className="far fa-calendar-alt mr-1"></i> {dateEnd || '-'}</div>
                                    </Col>
                                    <Col md={12} className="mb-3">
                                        <div className="text-muted small mb-1">Mô tả chi tiết</div>
                                        <div className="text-dark bg-light p-3 rounded-lg border border-light" style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                                            {item.ghiChu || 'Chưa có thông tin mô tả chi tiết cho khóa học này.'}
                                        </div>
                                    </Col>
                                </Row>

                                <hr className="my-5" />

                                <MaterialsAndTopicsTab
                                    id={id}
                                    topicsData={topicsData}
                                    isTopicsLoading={isTopicsLoading}
                                    refetchTopics={refetchTopics}
                                    materialsData={materialsData}
                                    isMaterialsLoading={isMaterialsLoading}
                                    refetchMaterials={refetchMaterials}
                                />

                                <hr className="my-5" />

                                <ScheduleTab
                                    contractId={contractId}
                                    donViData={donViData}
                                    nganhDaoTaoData={nganhDaoTaoData}
                                    studentsData={studentsData}
                                    tutorsData={tutorsData}
                                    currentUserSub={currentUserSub}
                                    currentUserRoles={currentUserRoles}
                                    isStudentUser={isStudentUser}
                                    isInstructorUser={isInstructorUser}
                                    scheduleData={scheduleData}
                                    isScheduleLoading={isScheduleLoading}
                                    refetchSchedule={refetchSchedule}
                                    adminRequestsData={adminRequestsData}
                                    isAdminRequestsLoading={isAdminRequestsLoading}
                                    mappingData={mappingData}
                                    refetchMappings={refetchMappings}
                                />
                            </div>
                        </Tab>

                        {/* TAB 2: DANH SÁCH NGƯỜI HƯỚNG DẪN */}
                        <Tab eventKey="tutors" title={<span><i className="fas fa-user-tie mr-2 text-primary"></i>Danh sách người hướng dẫn</span>}>
                            <TutorTab
                                contractId={contractId}
                                tutorsData={tutorsData}
                                isTutorsLoading={isTutorsLoading}
                                refetchTutors={refetchTutors}
                            />
                        </Tab>

                        {/* TAB 3: DANH SÁCH HỌC VIÊN */}
                        <Tab eventKey="students" title={<span><i className="fas fa-user-graduate mr-2 text-primary"></i>Danh sách học viên</span>}>
                            <StudentTab
                                contractId={contractId}
                                studentsData={studentsData}
                                isStudentsLoading={isStudentsLoading}
                                refetchStudents={refetchStudents}
                            />
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>
        </div>
    );
};

export default KhoaDaoTaoDetail;
